import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';

import PageFooter from 'src/components/PageFooter';
import PageHeader from 'src/components/PageHeader';
import StyledLinearProgressBar from 'src/components/StyledLinearProgressBar';
import { Address, useOnboardingWorkflow } from 'src/contexts/OnboardingWorkflowContext';
import { useTeamMemberData } from 'src/hooks/useTeamMemberData';
import { selectCareRecipient } from 'src/store/slices/onboardingSlice';

export const Route = createFileRoute('/(app)/onboarding/visit-address')({
    component: VisitAddressComponent,
});

function VisitAddressComponent() {
    const careRecipient = useSelector(selectCareRecipient);

    const { canGoBack, previousStep, nextStep, progress, address, setAddress, isProcessing } =
        useOnboardingWorkflow();
    const { currentTeam } = useTeamMemberData();

    const [isEditing, setIsEditing] = useState(false);

    const displayedCareRecipientName = careRecipient?.preferredName ?? careRecipient?.firstName;

    // Get address from teamConfigurations or existing address in context
    const teamAddress = useMemo(() => {
        const { address, addressAccessDetails } = currentTeam?.teamConfigurations ?? {};
        if (address) {
            return {
                addressLine1: address.addressLine1 || '',
                addressLine2: address.addressLine2 || '',
                city: address.city || '',
                state: address.state || '',
                zipCode: address.zipcode || '', // API uses 'zipcode', form uses 'zipCode'
                accessDetails: addressAccessDetails || '',
            };
        }
        return null;
    }, [currentTeam?.teamConfigurations]);

    const { control, handleSubmit, formState, getValues, watch, reset } = useForm<Address>({
        mode: 'onChange',
        defaultValues: {
            addressLine1: address?.addressLine1 || teamAddress?.addressLine1 || '',
            addressLine2: address?.addressLine2 || teamAddress?.addressLine2 || '',
            city: address?.city || teamAddress?.city || '',
            state: address?.state || teamAddress?.state || '',
            zipCode: address?.zipCode || teamAddress?.zipCode || '',
            accessDetails:
                address?.accessDetails ||
                teamAddress?.accessDetails ||
                currentTeam?.teamConfigurations?.addressAccessDetails ||
                '',
        },
    });

    const [addressLine1, addressLine2, city, state, zipCode] = watch([
        'addressLine1',
        'addressLine2',
        'city',
        'state',
        'zipCode',
        'accessDetails',
    ]);

    const onSubmit = async (data: Address) => {
        // Save address to context
        setAddress(data);
        // nextStep() will handle updating team address access details and creating the visit schedule
        await nextStep();
    };

    // Update form when team address becomes available
    useEffect(() => {
        if (teamAddress && !address) {
            reset({
                addressLine1: teamAddress.addressLine1,
                addressLine2: teamAddress.addressLine2,
                city: teamAddress.city,
                state: teamAddress.state,
                zipCode: teamAddress.zipCode,
                accessDetails:
                    teamAddress.accessDetails ||
                    currentTeam?.teamConfigurations?.addressAccessDetails ||
                    '',
            });
        }
    }, [teamAddress, address, reset, currentTeam?.teamConfigurations?.addressAccessDetails]);

    useEffect(() => {
        if (!isEditing) {
            const currentValues = getValues();
            const { addressLine1, city, state, zipCode } = currentValues;
            if (!addressLine1 && !city && !state && !zipCode) {
                setIsEditing(true);
            }
        }
    }, [isEditing, getValues]);

    return (
        <>
            <PageHeader
                hideBackButton={!canGoBack}
                onBack={canGoBack ? previousStep : undefined}
                hideActionButton
            >
                <StyledLinearProgressBar value={progress.percentage} />
            </PageHeader>

            <Stack sx={{ gap: 4, mb: 2.5 }}>
                <Stack sx={{ gap: 1.5 }}>
                    <Typography variant="h1" sx={{ fontWeight: 'bold' }}>
                        Where will care visits take place?
                    </Typography>

                    <Typography>
                        Provide the physical address where caregivers will visit and support{' '}
                        {displayedCareRecipientName}.
                    </Typography>
                </Stack>

                {!isEditing && (
                    <Stack sx={{ gap: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            Address on file
                        </Typography>

                        <Stack
                            direction="row"
                            sx={{
                                gap: 1.5,
                                py: 1.5,
                                px: 2.5,
                                border: '1px solid',
                                borderRadius: 3,
                                borderColor: 'grey.500',
                                alignItems: 'center',
                            }}
                        >
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2">
                                    {addressLine1} {addressLine2}
                                </Typography>
                                <Typography variant="body2">
                                    {city}, {state}, {zipCode}
                                </Typography>
                            </Box>

                            {/* TODO: add edit button when api supports updating the address */}
                        </Stack>
                    </Stack>
                )}

                {isEditing && (
                    <>
                        <Controller
                            name="addressLine1"
                            control={control}
                            rules={{ required: 'Address is required' }}
                            render={({
                                field: { onChange, value, ref },
                                fieldState: { error },
                            }) => (
                                <TextField
                                    inputRef={ref}
                                    fullWidth
                                    label="Address Line 1"
                                    variant="outlined"
                                    value={value}
                                    onChange={onChange}
                                    error={!!error}
                                    helperText={error?.message}
                                />
                            )}
                        />

                        <Controller
                            name="addressLine2"
                            control={control}
                            render={({
                                field: { onChange, value, ref },
                                fieldState: { error },
                            }) => (
                                <TextField
                                    inputRef={ref}
                                    fullWidth
                                    label="Apt, suite, unit (if applicable)"
                                    variant="outlined"
                                    value={value}
                                    onChange={onChange}
                                    error={!!error}
                                    helperText={error?.message}
                                />
                            )}
                        />

                        <Controller
                            name="city"
                            control={control}
                            rules={{ required: 'City is required' }}
                            render={({
                                field: { onChange, value, ref },
                                fieldState: { error },
                            }) => (
                                <TextField
                                    inputRef={ref}
                                    fullWidth
                                    label="City"
                                    variant="outlined"
                                    value={value}
                                    onChange={onChange}
                                    error={!!error}
                                    helperText={error?.message}
                                />
                            )}
                        />

                        {/* TODO: use validated inputs for 50 states */}
                        <Controller
                            name="state"
                            control={control}
                            rules={{ required: 'State is required' }}
                            render={({
                                field: { onChange, value, ref },
                                fieldState: { error },
                            }) => (
                                <TextField
                                    inputRef={ref}
                                    fullWidth
                                    label="State"
                                    variant="outlined"
                                    value={value}
                                    onChange={onChange}
                                    error={!!error}
                                    helperText={error?.message}
                                />
                            )}
                        />

                        <Controller
                            name="zipCode"
                            control={control}
                            rules={{ required: 'ZIP Code is required' }}
                            render={({
                                field: { onChange, value, ref },
                                fieldState: { error },
                            }) => (
                                <TextField
                                    inputRef={ref}
                                    fullWidth
                                    label="ZIP Code"
                                    variant="outlined"
                                    value={value}
                                    onChange={onChange}
                                    error={!!error}
                                    helperText={error?.message}
                                />
                            )}
                        />
                    </>
                )}

                <Stack sx={{ gap: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Access Details (optional)
                    </Typography>
                    <Controller
                        name="accessDetails"
                        control={control}
                        render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                            <TextField
                                inputRef={ref}
                                fullWidth
                                multiline
                                minRows={3}
                                label="Access Details"
                                placeholder="Add notes about how to enter the residence, parking rules, or any details about arriving for visits that are important for folks to know."
                                variant="outlined"
                                value={value}
                                onChange={onChange}
                                error={!!error}
                                helperText={error?.message}
                            />
                        )}
                    />
                </Stack>

                <Typography variant="subtitle1" sx={{ textDecoration: 'none' }}>
                    To update the address for where care visits will take place, call Sage Home
                    Support at <a href="tel:8564548564">856 454 8564</a> or email us at{' '}
                    <a href="mailto:sagehome@sagehealth.com">sagehome@sagehealth.com</a>.
                </Typography>
            </Stack>

            <PageFooter includeShadow>
                {isEditing ? (
                    <Button
                        variant="contained"
                        fullWidth
                        color="primary"
                        onClick={() => setIsEditing(false)}
                        disabled={!formState.isValid}
                    >
                        Update
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        fullWidth
                        color="primary"
                        onClick={handleSubmit(onSubmit)}
                        disabled={!formState.isValid || isProcessing}
                    >
                        {isProcessing ? 'Creating...' : 'Next'}
                    </Button>
                )}
            </PageFooter>
        </>
    );
}
